import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

const styles = theme => ({
  radioRoot: {
    position: 'reltive',
    width: 'auto',
    height: 'auto',
    color: theme.palette.secondary[800],
    fontFamily: theme.typography.fontFamily,
    borderColor: theme.palette.grey[600],
    borderStyle: 'solid',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderRadius: 0,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: 0,
      top: '50%',
      width: 1,
      height: 24,
      marginTop: -12,
      backgroundColor: theme.palette.grey[600],
      zIndex: '1',
    },
    '&:first-of-type': {
      borderLeftWidth: 1,
      borderRadius: '4px 0 0 4px',
      '&:before': {
        display: 'none',
      },
    },
    '&:last-of-type': {
      borderRightWidth: 1,
      borderRadius: '0 4px 4px 0',
    },
  },
  radioRootColorDefault: {
    backgroundColor: theme.palette.common.white,
  },
  radioRootColorPrimary: {
    backgroundColor: theme.palette.common.white,
  },
  radioChecked: {
    '&:before': {
      display: 'none',
    },
  },
  radioDisabled: {
    color: theme.palette.secondary[450],
  },
  label: {
    display: 'block',
    position: 'reltive',
    zIndex: '2',
    margin: -1,
    justifyContent: 'center',
    border: '1px solid transparent',
    borderRadius: 4,
    '&+input': {
      zIndex: '3',
    },
  },
  labelSizeXl: {
    fontSize: pxToRem(14),
    lineHeight: pxToEm(16, 14),
    letterSpacing: pxToEm(0.5),
    padding: '16px 15px 15px 15px',
  },
  labelSizeLg: {
    fontSize: pxToRem(14),
    lineHeight: pxToEm(16, 14),
    letterSpacing: pxToEm(0.5),
    padding: '11px 15px 11px 15px',
  },
  labelSizeMd: {
    fontSize: pxToRem(14),
    lineHeight: pxToEm(16, 14),
    letterSpacing: pxToEm(0.5),
    padding: '9px 15px 10px 15px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 7,
      paddingRight: 7,
    },
  },
  labelSizeSm: {
    fontSize: pxToRem(13),
    lineHeight: pxToEm(14, 13),
    letterSpacing: pxToEm(0.5),
    padding: '7px 13px 7px 13px',
  },
  labelCheckedColorDefault: {
    color: theme.palette.primary[500],
    borderColor: theme.palette.primary[500],
  },
  labelCheckedColorPrimary: {
    color: theme.palette.common.white,
    borderColor: theme.palette.primary[500],
    backgroundColor: theme.palette.primary[500],
    fontWeight: 700,
  },
});

const RadioButton = ({
  classes, label, size, color, ...otherProps
}) => (
  <Radio
    classes={{
      root: classNames(classes.radioRoot, {
        [classes.radioRootColorDefault]: color === 'default',
        [classes.radioRootColorPrimary]: color === 'primary',
      }),
      checked: classes.radioChecked,
      disabled: classes.radioDisabled,
    }}
    icon={
      <span
        className={classNames(classes.label, {
          [classes.labelSizeXl]: size === 'xl',
          [classes.labelSizeLg]: size === 'lg',
          [classes.labelSizeMd]: size === 'md',
          [classes.labelSizeSm]: size === 'sm',
        })}
      >
        {label}
      </span>
    }
    checkedIcon={
      <span
        className={classNames(classes.label, classes.labelChecked, {
          [classes.labelSizeXl]: size === 'xl',
          [classes.labelSizeLg]: size === 'lg',
          [classes.labelSizeMd]: size === 'md',
          [classes.labelSizeSm]: size === 'sm',
          [classes.labelCheckedColorDefault]: color === 'default',
          [classes.labelCheckedColorPrimary]: color === 'primary',
        })}
      >
        {label}
      </span>
    }
    {...otherProps}
  />
);

RadioButton.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  color: PropTypes.oneOf(['primary', 'default']),
  classes: PropTypes.shape({
    radioRoot: PropTypes.string.isRequired,
    radioRootColorDefault: PropTypes.string.isRequired,
    radioRootColorPrimary: PropTypes.string.isRequired,
    radioChecked: PropTypes.string.isRequired,
    radioDisabled: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    labelSizeLg: PropTypes.string.isRequired,
    labelSizeMd: PropTypes.string.isRequired,
    labelSizeSm: PropTypes.string.isRequired,
    labelCheckedColorDefault: PropTypes.string.isRequired,
    labelCheckedColorPrimary: PropTypes.string.isRequired,
  }).isRequired,
};

RadioButton.defaultProps = {
  value: '',
  checked: false,
  onChange: () => {},
  size: 'md',
  color: 'default',
};

export default withStyles(styles)(RadioButton);



// WEBPACK FOOTER //
// ./src/components/fields/RadioButtonField.js